import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { DataView } from 'primereact/dataview';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { exportDataTableCsv } from '../../helpers/exportCsv';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Fieldset } from 'primereact/fieldset';
import { FilterMatchMode } from 'primereact/api';
import Snackbar from '../../../components/Snackbar/Snackbar';
import { ISystem } from '../../helpers/interfaces';
import { connect } from 'react-redux';
import { getSystemsVeryShort, getInspections, updateInspection, closeUpdateInspectionSuccessNotification, closeUpdateInspectionFailNotification,
         addInspection, closeCreateInspectionSuccessNotification, closeCreateInspectionFailNotification, deleteInspection,
         getServiceActionsForInspection, clearInspectionServiceActions, addServiceAction, updateServiceAction, deleteServiceAction,
         addServiceActionAttachment, addServiceActionPartImage,
         closeUpdateServiceActionSuccessNotification, closeUpdateServiceActionFailNotification,
         closeCreateServiceActionSuccessNotification, closeCreateServiceActionFailNotification, getInspectionPerformers } from '../../stores/actions/installerActions';
import { arrayIncludes } from '@material-ui/pickers/_helpers/utils';
import ServiceActionFormDialog from './ServiceActionFormDialog';
import { performersFromIds, performerIdsOf } from './ServiceActionPerformers';
import BatteryReplacementFormDialog from './BatteryReplacementFormDialog';

interface IInspectionPerformer {
    id: number;
    name: string;
}

interface InspectionsProps {
	systems: ISystem[];
    performerOptions: IInspectionPerformer[] | null;
    getInspectionPerformers: () => void;
	mobileView: boolean;
    dbInspections: any,
    updateInspectionSuccess: boolean,
    updateInspectionFail: boolean,
    createInspectionSuccess: boolean,
    createInspectionFail: boolean,
    inspectionServiceActions: any,
    updateServiceActionSuccess: boolean,
    updateServiceActionFail: boolean,
    createServiceActionSuccess: boolean,
    createServiceActionFail: boolean,
	getSystemsVeryShort: () => void;
    getInspections: () => void;
    updateInspection: (inspection) => void;
    addInspection: (inspection) => Promise<any>;
    deleteInspection: (inspectionId) => void;
    closeUpdateInspectionSuccessNotification: () => void;
    closeUpdateInspectionFailNotification: () => void;
    closeCreateInspectionSuccessNotification: () => void;
    closeCreateInspectionFailNotification: () => void;
    getServiceActionsForInspection: (inspectionId) => void;
    clearInspectionServiceActions: () => void;
    addServiceAction: (serviceAction) => Promise<any>;
    updateServiceAction: (serviceAction) => void;
    deleteServiceAction: (serviceActionId) => void;
    addServiceActionAttachment: (attachment) => Promise<any>;
    addServiceActionPartImage: (image) => Promise<any>;
    closeUpdateServiceActionSuccessNotification: () => void;
    closeUpdateServiceActionFailNotification: () => void;
    closeCreateServiceActionSuccessNotification: () => void;
    closeCreateServiceActionFailNotification: () => void;
}

const Inspections = ({
	systems,
	mobileView,
    dbInspections,
    getInspections,
    getSystemsVeryShort,
    updateInspection,
    addInspection,
    deleteInspection,
    updateInspectionSuccess,
    updateInspectionFail,
    createInspectionSuccess,
    createInspectionFail,
    closeUpdateInspectionSuccessNotification,
    closeUpdateInspectionFailNotification,
    closeCreateInspectionSuccessNotification,
    closeCreateInspectionFailNotification,
    inspectionServiceActions,
    updateServiceActionSuccess,
    updateServiceActionFail,
    createServiceActionSuccess,
    createServiceActionFail,
    getServiceActionsForInspection,
    clearInspectionServiceActions,
    addServiceAction,
    updateServiceAction,
    deleteServiceAction,
    addServiceActionAttachment,
    addServiceActionPartImage,
    closeUpdateServiceActionSuccessNotification,
    closeUpdateServiceActionFailNotification,
    closeCreateServiceActionSuccessNotification,
    closeCreateServiceActionFailNotification,
    performerOptions,
    getInspectionPerformers
}: InspectionsProps) => {
    let emptyInspection = {
        ID: null,
        systemId: null,
        systemName: null,
        year: new Date().getFullYear(),
        performedAt: new Date(),
        protocolNumber: null,
        mode: 'on-line',
        performerIds: [],
        performers: [],
        underWarranty: false,
        offerNumber: null,
        offerCreatedAt: new Date(),
        orderNumber: null,
        orderCreatedAt: new Date(),
        status: 'planned'
    };

    useEffect(() => {
        getSystemsVeryShort();
        getInspectionPerformers();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (systems && dbInspections) {
            setInspections(dbInspections);
            const systemNamesByIds = Object.fromEntries(systems.map(x => [x.ID, x.name]));
            dbInspections?.forEach(insp =>{
                insp.systemName = systemNamesByIds[insp.systemId];
                // for filtering, sorting and export; old inspections only have the free text performedBy
                insp.performersLabel = insp.performers?.length ? insp.performers.map((p) => p.name).join(', ') : (insp.performedBy ?? null);
            });
        }

	}, [dbInspections, systems]);

	useEffect(() => {
		if (updateInspectionSuccess) {
            setTimeout(() => {
                closeUpdateInspectionSuccessNotification();
            }, 6000);
            setInspectionDialog(false);
		}
	}, [updateInspectionSuccess]);

    useEffect(() => {
		if (updateInspectionFail) {
            setTimeout(() => {
                closeUpdateInspectionFailNotification();
            }, 6000)
        }
	}, [updateInspectionFail]);


    useEffect(() => {
		if (createInspectionSuccess) {
            setTimeout(() => {
                closeCreateInspectionSuccessNotification();
            }, 6000);
			setInspectionDialog(false);
		}
	}, [createInspectionSuccess]);

    useEffect(() => {
		if (createInspectionFail) {
            setTimeout(() => {
                closeCreateInspectionFailNotification();
            }, 6000)
        }
	}, [createInspectionFail]);

    useEffect(() => {
        if (systems) {
            setSystemNames(systems.map(el => el.name).sort());
            getInspections();
        }
	}, [systems]);

    useEffect(() => {
        if (updateServiceActionSuccess) {
            setTimeout(() => {
                closeUpdateServiceActionSuccessNotification();
            }, 6000);
            setServiceActionDialog(false);
            setBatteryReplacementDialog(false);
        }
    }, [updateServiceActionSuccess]);

    useEffect(() => {
        if (updateServiceActionFail) {
            setTimeout(() => {
                closeUpdateServiceActionFailNotification();
            }, 6000)
        }
    }, [updateServiceActionFail]);

    useEffect(() => {
        if (createServiceActionSuccess) {
            setTimeout(() => {
                closeCreateServiceActionSuccessNotification();
            }, 6000);
            setServiceActionDialog(false);
            setBatteryReplacementDialog(false);
        }
    }, [createServiceActionSuccess]);

    useEffect(() => {
        if (createServiceActionFail) {
            setTimeout(() => {
                closeCreateServiceActionFailNotification();
            }, 6000)
        }
    }, [createServiceActionFail]);

    const dt = useRef(null);

    // from 4 years ahead down to the first inspections (2021), newest first
    const FIRST_INSPECTION_YEAR = 2021;
    const lastYear = new Date().getFullYear() + 4;
    const years = Array.from({ length: lastYear - FIRST_INSPECTION_YEAR + 1 }, (_, i) => lastYear - i);
    const modes = ['on-line', 'on-site'];
    const statuses = ['planned', 'in_progress', 'completed'];
    const [systemNames, setSystemNames] = useState([]);
    const [inspectionDialog, setInspectionDialog] = useState(false);
    const [deleteInspectionDialog, setDeleteInspectionDialog] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [inspections, setInspections] = useState(dbInspections);
    const [inspection, setInspection] = useState(emptyInspection);
    const [serviceActionDialog, setServiceActionDialog] = useState(false);
    const [batteryReplacementDialog, setBatteryReplacementDialog] = useState(false);
    const [deleteServiceActionDialog, setDeleteServiceActionDialog] = useState(false);
    const [serviceAction, setServiceAction] = useState(null);
    const [pendingServiceActions, setPendingServiceActions] = useState([]);
    const pendingIdCounter = useRef(0);

    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        systemName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        performersLabel: { value: null, matchMode: FilterMatchMode.CONTAINS },

    });

    const openNew = () => {
        setInspection(emptyInspection);
        clearInspectionServiceActions();
        setPendingServiceActions([]);
        setInspectionDialog(true);
    };

    // Mobile filter state
    const [mobileFilterDialog, setMobileFilterDialog] = useState(false);
    const [mobileFilters, setMobileFilters] = useState({
        systemName: '',
        performedBy: '',
        year: null,
        mode: null,
        status: null,
    });
    const [mobileSortField, setMobileSortField] = useState('ID');
    const [mobileSortOrder, setMobileSortOrder] = useState<-1 | 1>(-1);
    const [pendingFilters, setPendingFilters] = useState(mobileFilters);
    const [pendingSortField, setPendingSortField] = useState(mobileSortField);
    const [pendingSortOrder, setPendingSortOrder] = useState<-1 | 1>(mobileSortOrder);

    const openMobileFilterDialog = () => {
        setPendingFilters(mobileFilters);
        setPendingSortField(mobileSortField);
        setPendingSortOrder(mobileSortOrder);
        setMobileFilterDialog(true);
    };

    const applyMobileFilters = () => {
        setMobileFilters(pendingFilters);
        setMobileSortField(pendingSortField);
        setMobileSortOrder(pendingSortOrder);
        setMobileFilterDialog(false);
    };

    const resetMobileFilters = () => {
        const empty = {
            systemName: '', performedBy: '', year: null,
            mode: null, status: null,
        };
        setPendingFilters(empty);
        setPendingSortField('ID');
        setPendingSortOrder(-1);
    };

    // Filtered + sorted list
    const filteredInspections = useMemo(() => {
        if (!inspections) return [];
        let result = [...inspections];

        if (mobileFilters.systemName)
            result = result.filter(i => i.systemName?.toLowerCase().includes(mobileFilters.systemName.toLowerCase()));
        if (mobileFilters.performedBy)
            result = result.filter(i => i.performersLabel?.toLowerCase().includes(mobileFilters.performedBy.toLowerCase()));
        if (mobileFilters.year !== null)
            result = result.filter(i => i.year === mobileFilters.year);
        if (mobileFilters.mode)
            result = result.filter(i => i.mode === mobileFilters.mode);
        if (mobileFilters.status)
            result = result.filter(i => i.status === mobileFilters.status);

        result.sort((a, b) => {
            const aVal = a[mobileSortField] ?? '';
            const bVal = b[mobileSortField] ?? '';
            if (aVal < bVal) return -1 * mobileSortOrder;
            if (aVal > bVal) return 1 * mobileSortOrder;
            return 0;
        });

        return result;
    }, [inspections, mobileFilters, mobileSortField, mobileSortOrder]);

    // Badge count
    const activeFilterCount = useMemo(() => {
        return Object.entries(mobileFilters).filter(([key, v]) => {
            if (key === 'systemName') return false;
            if (Array.isArray(v)) return v.length > 0;
            return v !== '' && v !== null;
        }).length;
    }, [mobileFilters]);

    const sortOptions = [
        { label: 'ID', value: 'ID' },
        { label: 'System', value: 'systemName' },
        { label: 'Year', value: 'year' },
        { label: 'Performed at', value: 'performedAt' },
        { label: 'Protocol', value: 'protocolNumber' },
        { label: 'Mode', value: 'mode' },
        { label: 'Performed by', value: 'performersLabel' },
        { label: 'Offer number', value: 'offerNumber' },
        { label: 'Offer date', value: 'offerCreatedAt' },
        { label: 'Order number', value: 'orderNumber' },
        { label: 'Order date', value: 'orderCreatedAt' },
        { label: 'Status', value: 'status' },
    ];    

    const mobileFilterBar = (
        <div className="flex align-items-center gap-2 mb-3">
            <span className="p-input-icon-left flex-1">
                <InputText
                    value={mobileFilters.systemName}
                    onChange={(e) => setMobileFilters(prev => ({ ...prev, systemName: e.target.value }))}
                    placeholder="Search system..."
                    className="w-full"
                />
            </span>
            <div className="relative">
                <Button
                    icon="pi pi-filter"
                    rounded
                    outlined={activeFilterCount === 0}
                    severity={activeFilterCount > 0 ? 'info' : undefined}
                    onClick={openMobileFilterDialog}
                />
                {activeFilterCount > 0 && (
                    <span
                        className="absolute top-0 right-0 bg-primary text-white border-circle flex align-items-center justify-content-center"
                        style={{ width: '1.2rem', height: '1.2rem', fontSize: '0.65rem', transform: 'translate(30%, -30%)' }}
                    >
                        {activeFilterCount}
                    </span>
                )}
            </div>
            <Button icon="pi pi-plus" severity="success" rounded onClick={openNew} />
        </div>
    );

    const hideDialog = () => {
        setInspectionDialog(false);
        clearInspectionServiceActions();
        setPendingServiceActions([]);
    };

    const hideDeleteInspectionDialog = () => {
        setDeleteInspectionDialog(false);
    };

    const saveInspection = async () => {
        if (!inspection.systemName) {
            return;
        }
        const systemIdsByNames = Object.fromEntries(systems.map(x => [x.name, x.ID]));
        inspection.systemId = systemIdsByNames[inspection.systemName];
        // performers sent back with the saved inspection, so that the list shows them without reloading
        const knownPerformers = [...(Array.isArray(performerOptions) ? performerOptions : []), ...(inspection.performers ?? [])];
        inspection.performers = (inspection.performerIds ?? [])
            .map((id) => knownPerformers.find((p) => p.id === id))
            .filter(Boolean)
            .sort((a, b) => a.name.localeCompare(b.name));
        inspection.performersLabel = inspection.performers.length ? inspection.performers.map((p) => p.name).join(', ') : (inspection.performedBy ?? null);

        if (inspection.ID) {
            updateInspection(inspection);
            return;
        }

        const created = await addInspection(inspection);
        if (!created || !created.ID) {
            return;
        }

        for (const sa of pendingServiceActions) {
            const pendingAttachments = sa.pendingAttachments;
            const pendingPartImages = sa.pendingPartImages;
            const rest = { ...sa };
            delete rest.ID; // drop the temporary id, let the backend assign a real one
            delete rest.pendingAttachments;
            delete rest.pendingPartImages;
            const createdSa = await addServiceAction({
                ...rest,
                systemId: inspection.systemId,
                systemName: inspection.systemName,
                inspectionId: created.ID,
            });
            if (createdSa && createdSa.ID && pendingAttachments && pendingAttachments.length > 0) {
                for (const att of pendingAttachments) {
                    await addServiceActionAttachment({
                        serviceActionId: createdSa.ID,
                        fileData: att.fileData,
                        fileMimeType: att.fileMimeType,
                        fileSize: att.fileSize,
                        fileName: att.fileName,
                    });
                }
            }
            if (createdSa && createdSa.ID && pendingPartImages && pendingPartImages.length > 0) {
                for (const img of pendingPartImages) {
                    await addServiceActionPartImage({
                        serviceActionId: createdSa.ID,
                        partKind: img.partKind,
                        imageData: img.imageData,
                        imageFileType: img.imageFileType,
                    });
                }
            }
        }
        setPendingServiceActions([]);
    };

    const editInspection = (inspection) => {
        setInspection({ ...inspection, performerIds: (inspection.performers ?? []).map((p) => p.id) });
        setInspectionDialog(true);
        getServiceActionsForInspection(inspection.ID);
    };

    const confirmDeleteInspection = (inspection) => {
        setInspection(inspection);
        setDeleteInspectionDialog(true);
    };

    const deleteInspectionItem = () => {
        deleteInspection(inspection.ID);
        let _inspections = inspections.filter((val) => val.ID !== inspection.ID);
        setInspections(_inspections);
        setDeleteInspectionDialog(false);
        setInspection(emptyInspection);
    };

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value);
        let _inspection = { ...inspection };
        if (arrayIncludes(['performedAt', 'offerCreatedAt', 'orderCreatedAt'], name)) {
            val.setMinutes(val.getMinutes() - val.getTimezoneOffset());
        }
        _inspection[`${name}`] = name === 'performerIds' ? e.value : val;
        setInspection(_inspection);
    };

    // one tag per performer, one under another; old inspections show their free text in gray
    const performersTemplate = (rowData) => {
        if (rowData.performers?.length) {
            return (
                <div className="flex flex-column align-items-start gap-1">
                    {rowData.performers.map((p) => <Tag key={p.id} value={p.name} />)}
                </div>
            );
        }
        return rowData.performedBy ? <Tag value={rowData.performedBy} severity="secondary" /> : null;
    };

    function formatDateRemoveTime(date) {
        return date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, "0") + "-" + String(date.getDate()).padStart(2, "0");
    }

    // Service actions performed as part of an inspection should have the same performed at date (?)
    const inspectionServiceActionDate = () => formatDateRemoveTime(new Date(inspection.performedAt));

    const emptyServiceActionForInspection = () => ({
        ID: null,
        date: inspectionServiceActionDate(),
        systemId: inspection.systemId,
        systemName: inspection.systemName,
        removedPartDesc: null,
        removedPartSerialNumber: null,
        installedPartDesc: null,
        installedPartSerialNumber: null,
        notes: null,
        underWarranty: false,
        removedPartImageData: null,
        removedPartImageFileType: null,
        installedPartImageData: null,
        installedPartImageFileType: null,
        removedPartName: null,
        installedPartName: null,
        inspectionId: inspection.ID,
        batteryReplacement: false,
        // by default performed by the inspection performers, can be changed
        performerIds: inspection.performerIds ?? [],
        performers: performersFromIds(inspection.performerIds, performerOptions, inspection.performers),
        pendingAttachments: [],
        pendingPartImages: [],
    });

    const openNewServiceAction = () => {
        setServiceAction(emptyServiceActionForInspection());
        setServiceActionDialog(true);
    };

    const emptyBatteryReplacementForInspection = () => ({
        ...emptyServiceActionForInspection(),
        batteryReplacement: true,
        batteryReplacementPlanned: true,
    });

    const openNewBatteryReplacement = () => {
        setServiceAction(emptyBatteryReplacementForInspection());
        setBatteryReplacementDialog(true);
    };

    const editServiceActionItem = (sa) => {
        setServiceAction({ ...sa, performerIds: performerIdsOf(sa), date: inspectionServiceActionDate() });
        if (sa.batteryReplacement) {
            setBatteryReplacementDialog(true);
        } else {
            setServiceActionDialog(true);
        }
    };

    const hideServiceActionDialog = () => {
        setServiceActionDialog(false);
    };

    const hideBatteryReplacementDialog = () => {
        setBatteryReplacementDialog(false);
    };

    const saveServiceActionItem = async () => {
        if (!serviceAction.batteryReplacement && !serviceAction.removedPartDesc && !serviceAction.installedPartDesc) {
            return;
        }

        if (!inspection.ID) {
            // Inspection isn't saved yet: stage locally (attachments included), send it
            // all along once the inspection is created (see saveInspection).
            const staged = { ...serviceAction, systemId: inspection.systemId, systemName: inspection.systemName, date: inspectionServiceActionDate() };
            if (staged.ID) {
                setPendingServiceActions(pendingServiceActions.map((sa) => sa.ID === staged.ID ? staged : sa));
            } else {
                pendingIdCounter.current -= 1;
                staged.ID = pendingIdCounter.current;
                setPendingServiceActions([...pendingServiceActions, staged]);
            }
            setServiceActionDialog(false);
            setBatteryReplacementDialog(false);
            return;
        }

        const { pendingAttachments, pendingPartImages, ...rest } = serviceAction;
        const toSave = { ...rest, systemId: inspection.systemId, systemName: inspection.systemName, inspectionId: inspection.ID, date: inspectionServiceActionDate() };

        if (toSave.ID > 0) {
            updateServiceAction(toSave);
            return;
        }

        const created = await addServiceAction(toSave);
        if (created && created.ID && pendingAttachments && pendingAttachments.length > 0) {
            for (const att of pendingAttachments) {
                await addServiceActionAttachment({
                    serviceActionId: created.ID,
                    fileData: att.fileData,
                    fileMimeType: att.fileMimeType,
                    fileSize: att.fileSize,
                    fileName: att.fileName,
                });
            }
        }
        if (created && created.ID && pendingPartImages && pendingPartImages.length > 0) {
            for (const img of pendingPartImages) {
                await addServiceActionPartImage({
                    serviceActionId: created.ID,
                    partKind: img.partKind,
                    imageData: img.imageData,
                    imageFileType: img.imageFileType,
                });
            }
        }
    };

    const confirmDeleteServiceActionItem = (sa) => {
        setServiceAction(sa);
        setDeleteServiceActionDialog(true);
    };

    const hideDeleteServiceActionDialog = () => {
        setDeleteServiceActionDialog(false);
    };

    const deleteServiceActionItem = () => {
        if (serviceAction.ID < 0) {
            // staged, never persisted: just drop it locally
            setPendingServiceActions(pendingServiceActions.filter((sa) => sa.ID !== serviceAction.ID));
        } else {
            deleteServiceAction(serviceAction.ID);
        }
        setDeleteServiceActionDialog(false);
    };

    const saDateTemplate = (rowData) => {
        if (rowData.date) {
            return (new Date(rowData.date)).toLocaleDateString('sv-SE');
        }
        return null;
    };

    const saUnderWarrantyTemplate = (rowData) => {
        return <Tag value={rowData.underWarranty ? 'YES' : 'NO'} severity={rowData.underWarranty ? 'success' : 'info'}></Tag>
    };

    const saActionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => editServiceActionItem(rowData)} />
                <Button icon="pi pi-trash" rounded outlined severity="danger" onClick={() => confirmDeleteServiceActionItem(rowData)} />
            </React.Fragment>
        );
    };


    const statusTemplate = (rowData) => {
        return <Tag value={rowData.status} severity={getSeverity(rowData)}></Tag>;
    };

    const modeTemplate = (rowData) => {
        const onSite = rowData.mode === 'on-site';
        return <Tag value={onSite ? 'on-site' : 'on-line'} severity={onSite ? 'warning' : 'info'}></Tag>
    };

    const performedAtTemplate = (rowData) => {
        if (rowData.performedAt) {
            return (new Date(rowData.performedAt)).toLocaleDateString('sv-SE');
        } 
        return null;
    };

    const offerCreatedAtTemplate = (rowData) => {
        if (rowData.offerCreatedAt) {
            return (new Date(rowData.offerCreatedAt)).toLocaleDateString('sv-SE');
        } 
        return null;
    };

    const orderCreatedAtTemplate = (rowData) => {
        if (rowData.orderCreatedAt) {
            return (new Date(rowData.orderCreatedAt)).toLocaleDateString('sv-SE');
        } 
        return null;
    };

    const cardTemplate = (rowData) => {
        return (
            <div className="col-12">
                <div className="border-1 surface-border border-round p-3 mb-2 surface-card">

                    <div className="flex justify-content-between align-items-center pb-2 mb-3 border-bottom-1 surface-border">
                        <span className="font-bold text-lg">{rowData.ID}</span>
                    </div>

                    <div className="mb-3 p-2 surface-50 border-round">
                        <div className="grid">
                            <div className="col-7">
                                <div className="text-xs text-color-secondary">System</div>
                                <div className="font-medium">{rowData.systemName || '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5"></div>
                        </div>
                    </div>

                    <div className="mb-3 p-2 surface-50 border-round">
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Year</div>
                                <div className="font-medium">{rowData.year || '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Performed at</div>
                                <div className="font-medium">{performedAtTemplate(rowData)}</div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-3 p-2 surface-50 border-round">
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Protocol number</div>
                                <div className="font-medium">{rowData.protocolNumber || '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Mode</div>
                                <div className="font-medium">{modeTemplate(rowData)}</div>
                            </div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Performed by</div>
                                <div className="font-medium">{performersTemplate(rowData) ?? '---'}</div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-3 p-2 surface-50 border-round">
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Offer number</div>
                                <div className="font-medium">{rowData.offerNumber || '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Offer created at</div>
                                <div className="font-medium">{offerCreatedAtTemplate(rowData)}</div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-3 p-2 surface-50 border-round">
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Order number</div>
                                <div className="font-medium">{rowData.orderNumber || '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Order created at</div>
                                <div className="font-medium">{orderCreatedAtTemplate(rowData)}</div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-content-between align-items-center pt-2 border-top-1 surface-border">
                        <div>
                            <div className="text-xs text-color-secondary">Status</div>
                            <div className="font-medium">{statusTemplate(rowData)}</div>
                        </div>
                        <div>
                            {actionBodyTemplate(rowData)}
                        </div>
                    </div>

                </div>
            </div>
        );
    };

    const mobileFilterDialogFooter = (
        <div className="flex justify-content-between">
            <Button label="Reset" icon="pi pi-times" text severity="secondary" onClick={resetMobileFilters} />
            <div className="flex gap-2">
                <Button label="Cancel" icon="pi pi-times" outlined onClick={() => setMobileFilterDialog(false)} />
                <Button label="Apply" icon="pi pi-check" onClick={applyMobileFilters} />
            </div>
        </div>
    );

    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => editInspection(rowData)} />
                <Button icon="pi pi-trash" rounded outlined severity="danger" onClick={() => confirmDeleteInspection(rowData)} />
            </React.Fragment>
        );
    };

    const getSeverity = (inspection) => {
        switch (inspection.status) {
            case 'planned':
                return 'info';

            case 'in_progress':
                return 'warning';

            case 'completed':
                return 'success';

            default:
                return null;
        }
    };

    const rightToolbarTemplate = () => {
        return (
            <div className="flex flex-wrap gap-2">
                <Button label="Export" icon="pi pi-file-export" outlined onClick={() => exportDataTableCsv(dt, 'inspections')} />
            </div>
        );
    };

    const leftToolbarTemplate = () => {
        return (
            <div className="flex flex-wrap gap-2">
                <Button label="New" icon="pi pi-plus" severity="success" onClick={openNew} />
            </div>
        );
    };
    const inspectionDialogFooter = (
        <React.Fragment>
            <Button label="Cancel" icon="pi pi-times" outlined onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" onClick={saveInspection} />
        </React.Fragment>
    );
    const deleteInspectionDialogFooter = (
        <React.Fragment>
            <Button label="No" icon="pi pi-times" outlined onClick={hideDeleteInspectionDialog} />
            <Button label="Yes" icon="pi pi-check" severity="danger" onClick={deleteInspectionItem} />
        </React.Fragment>
    );
    const deleteServiceActionDialogFooter = (
        <React.Fragment>
            <Button label="No" icon="pi pi-times" outlined onClick={hideDeleteServiceActionDialog} />
            <Button label="Yes" icon="pi pi-check" severity="danger" onClick={deleteServiceActionItem} />
        </React.Fragment>
    );

    const dataView = useMemo(() => {
        return (
            <div>
                {mobileFilterBar}
                <DataView
                    value={filteredInspections}
                    itemTemplate={cardTemplate}
                    paginator
                    rows={10}
                />
            </div>
        );
    }, [filteredInspections, mobileFilters, mobileFilterDialog, createInspectionSuccess, updateInspectionSuccess]);

    const dataTable = useMemo(() => {
        return (
            <div className="card">
                <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

                <DataTable ref={dt} value={inspections} sortField="ID" sortOrder={-1}
                        dataKey="ID"  paginator rows={25} rowsPerPageOptions={[10, 25, 50, 100]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items" globalFilter={globalFilter} 
                        filters={filters} filterDisplay="row" stripedRows size='small' resizableColumns
                        exportFilename="inspections"
                        exportFunction={(e) => {
                            switch (e.field) {
                                case 'performedAt': return performedAtTemplate(e.rowData) ?? '';
                                case 'offerCreatedAt': return offerCreatedAtTemplate(e.rowData) ?? '';
                                case 'orderCreatedAt': return orderCreatedAtTemplate(e.rowData) ?? '';
                                default: return e.data;
                            }
                        }} >
                <Column exportable={false}></Column>
                <Column                    
                    field="ID" 
                    header="ID"
                    style={{maxWidth: '45px'}}
                    sortable>
                </Column>
                <Column 
                    field="systemName" 
                    header="System"
                    sortable
                    filter 
                    filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="year" 
                    header="Year" 
                    sortable>
                </Column>
                <Column 
                    field="performedAt" 
                    header="Performed"
                    body={performedAtTemplate}
                    sortable>                
                </Column>
                <Column 
                    field="protocolNumber" 
                    header="Protocol" 
                    sortable>                
                </Column>
                <Column 
                    field="mode" 
                    header="Mode" 
                    body={modeTemplate} 
                    sortable>                
                </Column>
                <Column 
                    field="performersLabel" 
                    header="Performed by"
                    body={performersTemplate}
                    filter 
                    filterPlaceholder="" 
                    showFilterMenu={false}
                    sortable>                
                </Column>
                <Column
                    field="offerNumber"
                    header="Offer"
                    style={{maxWidth: '100px'}}
                    sortable>                
                </Column>
                <Column 
                    field="offerCreatedAt" 
                    header="Offer date"
                    body={offerCreatedAtTemplate}
                    sortable>                
                </Column>
                <Column 
                    field="orderNumber" 
                    header="Order"
                    sortable>
                </Column>
                <Column 
                    field="orderCreatedAt" 
                    header="Order date"
                    body={orderCreatedAtTemplate}
                    sortable>                
                </Column>
                <Column 
                    field="status" 
                    header="Status" 
                    body={statusTemplate} 
                    sortable>                
                </Column>
                <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '12rem', maxWidth: '24rem' }}></Column>
                </DataTable>
            </div>
        );
    }, [inspections]);


    return (
        <div>
            { mobileView ? dataView : dataTable }

            <Dialog visible={inspectionDialog}
                    style={{ width: mobileView ? '100%' : '40rem' }}
                    breakpoints={{ '960px': '75vw', '641px': '90vw' }} 
                    modal className="p-fluid" footer={inspectionDialogFooter} onHide={hideDialog} 
                    header={inspection.ID ? 'Edit item #' + inspection.ID : 'Add new item'}
                    >
                <div className="field">
                    <label className="mb-3 font-bold">System</label>
                    <Dropdown value={ inspection.systemName } onChange={(e) => onInputChange(e, 'systemName')} options={systemNames} optionLabel="name" 
                        filter placeholder="Select system" className="w-full md:w-28rem" invalid={!inspection.systemName}/>
                </div>
                <div className="formgrid grid">
                    <div className="field col">
                        <label className="mb-3 font-bold">Year</label>
                        <Dropdown value={inspection.year }  onChange={(e) => onInputChange(e, 'year')} options={years} optionLabel="name" 
                            placeholder="Select year"/>
                    </div>
                    <div className="field col">
                        <label className="mb-3 font-bold">Mode</label>
                        <Dropdown value={inspection.mode } onChange={(e) => onInputChange(e, 'mode')} options={modes} optionLabel="name" 
                            placeholder="Select mode" />
                    </div>
                    <div className="field col">
                        <label className="mb-3 font-bold">Status</label>
                        <Dropdown value={inspection.status } onChange={(e) => onInputChange(e, 'status')} options={statuses} optionLabel="name" 
                            placeholder="Select status" />
                    </div>
                </div>
                <div className="formgrid grid">
                    <div className="field col">
                        <label className="mb-3 font-bold">Performed by</label>
                        <MultiSelect value={inspection.performerIds ?? []} onChange={(e) => onInputChange(e, 'performerIds')}
                            options={Array.isArray(performerOptions) ? performerOptions : []} optionLabel="name" optionValue="id"
                            display="chip" filter placeholder="Select performers" emptyMessage="No users" />
                        {!inspection.performerIds?.length && inspection.performedBy && (
                            <small className="text-color-secondary">Previously entered: {inspection.performedBy}</small>
                        )}
                    </div> 
                    <div className="field col">   
                        <label className="mb-3 font-bold">Performed at</label>
                        <Calendar value={new Date(inspection.performedAt)} onChange={(e) => onInputChange(e, 'performedAt')} dateFormat="yy-mm-dd" />
                    </div>
                </div>
                <div className="field">
                    <label className="mb-3 font-bold">Protocol number</label>
                    <InputText value={inspection.protocolNumber} maxLength={15} onChange={(e) => onInputChange(e, 'protocolNumber')} />
                </div>
                <div className="formgrid grid">
                    <div className="field col">
                        <label className="mb-3 font-bold">Offer number</label>
                        <InputText value={inspection.offerNumber} maxLength={15} onChange={(e) => onInputChange(e, 'offerNumber')} />
                    </div>
                    <div className="field col">
                        <label className="mb-3 font-bold">Offer date</label>
                        <Calendar value={new Date(inspection.offerCreatedAt)} onChange={(e) => onInputChange(e, 'offerCreatedAt')} dateFormat="yy-mm-dd" />
                    </div>
                </div>
                <div className="formgrid grid">
                    <div className="field col">                    
                        <label className="mb-3 font-bold">Order number</label>
                        <InputText value={inspection.orderNumber} maxLength={15} onChange={(e) => onInputChange(e, 'orderNumber')} />
                    </div> 
                    <div className="field col">                    
                        <label className="mb-3 font-bold">Order date</label>
                        <Calendar value={new Date(inspection.orderCreatedAt)} onChange={(e) => onInputChange(e, 'orderCreatedAt')} dateFormat="yy-mm-dd" />
                    </div>
                </div>
                <div className="field" style={{ marginTop: '20px' }}>
                    <Fieldset legend="Service actions">
                        {!inspection.systemName && (
                            <small className="text-color-secondary block mb-2">
                                Please pick System first.
                            </small>
                        )}
                        <div className="flex justify-content-end gap-2 mb-3">
                            <Button label="Battery replacement" icon="pi pi-plus" severity="warning" onClick={openNewBatteryReplacement} disabled={!inspection.systemName} />
                            <Button label="Add service action" icon="pi pi-plus" severity="success" onClick={openNewServiceAction} disabled={!inspection.systemName} />
                        </div>
                        {(() => {
                            const items = inspection.ID ? inspectionServiceActions : pendingServiceActions;
                            if (!items || items.length === 0) {
                                return <small className="text-color-secondary">No service actions yet.</small>;
                            }
                            // A DataTable's fixed columns don't fit a narrow dialog without a
                            // horizontal scrollbar, so this list uses wrapping flex rows instead.
                            return (
                                <div className="flex flex-column gap-2">
                                    {items.map((sa) => (
                                        <div key={sa.ID} className="border-1 surface-border border-round p-2 flex justify-content-between align-items-center flex-wrap gap-2">
                                            <div style={{ minWidth: 0 }}>
                                                <div className="text-xs text-color-secondary">{saDateTemplate(sa)}</div>
                                                {sa.batteryReplacement ? (
                                                    <div className="flex align-items-center gap-2 flex-wrap">
                                                        <Tag value="CU battery replacement" severity="warning"></Tag>
                                                        <Tag value={sa.batteryReplacementPlanned ? 'Planned' : 'Malfunctioned'} severity={sa.batteryReplacementPlanned ? 'info' : 'danger'}></Tag>
                                                    </div>
                                                ) : (
                                                    <React.Fragment>
                                                        <div className="font-medium" style={{ wordBreak: 'break-word' }}>
                                                            Removed: {sa.removedPartDesc || sa.removedPartName || '---'}
                                                        </div>
                                                        <div className="font-medium" style={{ wordBreak: 'break-word' }}>
                                                            Installed: {sa.installedPartDesc || sa.installedPartName || '---'}
                                                        </div>
                                                    </React.Fragment>
                                                )}
                                            </div>
                                            <div className="flex align-items-center gap-2">
                                                {!sa.batteryReplacement && (
                                                    <div className="flex flex-column align-items-start">
                                                        <span className="text-xs text-color-secondary">Under warranty</span>
                                                        {saUnderWarrantyTemplate(sa)}
                                                    </div>
                                                )}
                                                {saActionBodyTemplate(sa)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}
                    </Fieldset>
                </div>
            </Dialog>

            <ServiceActionFormDialog
                visible={serviceActionDialog}
                mobileView={mobileView}
                title={serviceAction?.ID ? 'Edit service action #' + serviceAction.ID : 'Add service action'}
                value={serviceAction ?? {}}
                onChange={(patch) => setServiceAction({ ...serviceAction, ...patch })}
                onHide={hideServiceActionDialog}
                onSave={saveServiceActionItem}
                fixedSystemName={inspection.systemName}
                fixedDate={inspectionServiceActionDate()}
            />

            <BatteryReplacementFormDialog
                visible={batteryReplacementDialog}
                mobileView={mobileView}
                title={serviceAction?.ID ? 'Edit battery replacement #' + serviceAction.ID : 'Add battery replacement'}
                value={serviceAction ?? {}}
                onChange={(patch) => setServiceAction({ ...serviceAction, ...patch })}
                onHide={hideBatteryReplacementDialog}
                onSave={saveServiceActionItem}
                fixedSystemName={inspection.systemName}
                fixedDate={inspectionServiceActionDate()}
            />

            <Dialog visible={deleteServiceActionDialog} style={{ width: '32rem' }} breakpoints={{ '960px': '75vw', '641px': '90vw' }} header="Confirm" modal footer={deleteServiceActionDialogFooter} onHide={hideDeleteServiceActionDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    {serviceAction && (
                        <span>
                            Are you sure you want to delete this service action (#{serviceAction.ID})?
                        </span>
                    )}
                </div>
            </Dialog>

            <Dialog visible={deleteInspectionDialog} style={{ width: '32rem' }} breakpoints={{ '960px': '75vw', '641px': '90vw' }} header="Confirm" modal footer={deleteInspectionDialogFooter} onHide={hideDeleteInspectionDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    {inspection && (
                        <span>
                            Are you sure you want to delete this item (#{inspection.ID})?
                        </span>
                    )}
                </div>
            </Dialog>

            <Dialog
                visible={mobileFilterDialog}
                onHide={() => setMobileFilterDialog(false)}
                header="Filter & Sort"
                style={{ width: '95vw' }}
                footer={mobileFilterDialogFooter}
                modal
                className="p-fluid"
            >
                <div className="mb-4">
                    <label className="font-bold mb-2 block">Sort by</label>
                    <div className="flex gap-2">
                        <Dropdown
                            value={pendingSortField}
                            options={sortOptions}
                            onChange={(e) => setPendingSortField(e.value)}
                            className="flex-1"
                        />
                        <Button
                            icon={pendingSortOrder === 1 ? 'pi pi-sort-amount-up' : 'pi pi-sort-amount-down'}
                            outlined
                            onClick={() => setPendingSortOrder(prev => prev === 1 ? -1 : 1)}
                            tooltip={pendingSortOrder === 1 ? 'Ascending' : 'Descending'}
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Performed by</label>
                    <InputText
                        value={pendingFilters.performedBy}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, performedBy: e.target.value }))}
                        placeholder="Search..."
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Year</label>
                    <Dropdown
                        value={pendingFilters.year}
                        options={years.map(y => ({ label: String(y), value: y }))}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, year: e.value }))}
                        placeholder="Any" showClear className="w-full"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Mode</label>
                    <Dropdown
                        value={pendingFilters.mode}
                        options={modes.map(m => ({ label: m, value: m }))}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, mode: e.value }))}
                        placeholder="Any" showClear className="w-full"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Status</label>
                    <Dropdown
                        value={pendingFilters.status}
                        options={statuses.map(s => ({ label: s, value: s }))}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, status: e.value }))}
                        placeholder="Any" showClear className="w-full"
                    />
                </div>
            </Dialog>

            <Snackbar
                close
                closeNotification={() => closeUpdateInspectionSuccessNotification()}
                color='success'
                message={'Inspection updated'}
                open={updateInspectionSuccess}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeUpdateInspectionFailNotification()}
                color='danger'
                message={'Update error'}
                open={updateInspectionFail}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeCreateInspectionSuccessNotification()}
                color='success'
                message={'Inspection created'}
                open={createInspectionSuccess}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeCreateInspectionFailNotification()}
                color='danger'
                message={'Create error'}
                open={createInspectionFail}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeUpdateServiceActionSuccessNotification()}
                color='success'
                message={'Service action updated'}
                open={updateServiceActionSuccess}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeUpdateServiceActionFailNotification()}
                color='danger'
                message={'Service action update error'}
                open={updateServiceActionFail}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeCreateServiceActionSuccessNotification()}
                color='success'
                message={'Service action created'}
                open={createServiceActionSuccess}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeCreateServiceActionFailNotification()}
                color='danger'
                message={'Service action create error'}
                open={createServiceActionFail}
                place='tc'
            />
        </div>
    );
}


const mapStateToProps = (state) => {
	return {
		systems: state.dashboard.systemsVeryShort,
		mobileView: state.dashboard.mobileView,
		dbInspections: state.dashboard.inspections,
        updateInspectionSuccess: state.dashboard.updateInspectionSuccess,
        updateInspectionFail: state.dashboard.updateInspectionFail,
        createInspectionSuccess: state.dashboard.createInspectionSuccess,
        createInspectionFail: state.dashboard.createInspectionFail,
        inspectionServiceActions: state.dashboard.inspectionServiceActions,
        updateServiceActionSuccess: state.dashboard.updateServiceActionSuccess,
        updateServiceActionFail: state.dashboard.updateServiceActionFail,
        createServiceActionSuccess: state.dashboard.createServiceActionSuccess,
        createServiceActionFail: state.dashboard.createServiceActionFail,
        performerOptions: state.dashboard.inspectionPerformers
	};
};

const actions = {
	getSystemsVeryShort,
    getInspections,
    updateInspection,
    addInspection,
    deleteInspection,
    closeUpdateInspectionSuccessNotification,
    closeUpdateInspectionFailNotification,
    closeCreateInspectionSuccessNotification,
    closeCreateInspectionFailNotification,
    getServiceActionsForInspection,
    clearInspectionServiceActions,
    addServiceAction,
    updateServiceAction,
    deleteServiceAction,
    addServiceActionAttachment,
    addServiceActionPartImage,
    closeUpdateServiceActionSuccessNotification,
    closeUpdateServiceActionFailNotification,
    closeCreateServiceActionSuccessNotification,
    closeCreateServiceActionFailNotification,
    getInspectionPerformers
};

export default connect(mapStateToProps, actions)(Inspections);
